import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from '../../../components/shared/toast';
import en from '../../../translations/en.json';
import { apiClient } from '../../../api/apiClient';
import { 
  GET_RECORD_LABEL_DROPDOWN_URL, 
  GET_SONG_DROPDOWN_URL,
  GET_ALBUM_DETAILS_URL,
  GET_ALBUM_SONGS_URL 
} from '../../../api/album';
import './AlbumForm.css';

function LazyDropdown({ label, placeholder, fetchUrl, isMulti = false, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch dropdown data lazily
  const loadOptions = useCallback(async (targetPage, searchQuery, append = false) => {
    try {
      setIsLoading(true);
      const url = `${fetchUrl}?page=${targetPage}&limit=20&search=${encodeURIComponent(searchQuery)}`;
      const res = await apiClient(url);
      if (res) {
        const items = res.labels || res.songs || res.data || [];
        const formatted = items.map(item => ({
          id: item.id,
          name: item.name || item.display_name || item.title || `Item #${item.id}`
        }));

        setOptions(prev => append ? [...prev, ...formatted] : formatted);
        setHasMore(res.hasMore !== undefined ? res.hasMore : (targetPage < (res.totalPages || 1)));
      }
    } catch (err) {
      console.error('Failed to load dropdown data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchUrl]);

  // Trigger initial fetch on FIRST user click open
  const handleToggleOpen = () => {
    if (!isOpen && !hasOpened) {
      setHasOpened(true);
      loadOptions(1, '');
    }
    setIsOpen(prev => !prev);
  };

  // Handle search change
  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearch(q);
    setPage(1);
    loadOptions(1, q, false);
  };

  // Handle infinite scroll
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 20 && hasMore && !isLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadOptions(nextPage, search, true);
    }
  };

  // Select / Deselect handler
  const handleSelectOption = (opt) => {
    if (isMulti) {
      const currentArr = Array.isArray(value) ? value : [];
      const exists = currentArr.some(item => String(item.id) === String(opt.id));
      if (exists) {
        onChange(currentArr.filter(item => String(item.id) !== String(opt.id)));
      } else {
        onChange([...currentArr, opt]);
      }
    } else {
      onChange(opt);
      setIsOpen(false);
    }
  };

  const isSelected = (optId) => {
    if (isMulti) {
      return Array.isArray(value) && value.some(item => String(item.id) === String(optId));
    }
    return value && String(value.id) === String(optId);
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    onChange(isMulti ? [] : null);
  };

  return (
    <div className="form-field-group" ref={dropdownRef} style={{ position: 'relative' }}>
      <label className="form-field-label">
        {label}
        <span className="required-asterisk">*</span>
      </label>

      {/* Modern Trigger Box */}
      <div 
        className="dropdown-modern-trigger"
        onClick={handleToggleOpen}
        style={{
          cursor: 'pointer',
          display: 'flex',
          justify: 'space-between',
          alignItems: 'center',
          minHeight: '44px',
          height: 'auto',
          padding: '8px 14px',
          background: '#ffffff',
          borderRadius: '10px',
          border: isOpen ? '1px solid #3b82f6' : '1px solid #cbd5e1',
          boxShadow: isOpen ? '0 0 0 3px rgba(59, 130, 246, 0.12)' : 'none',
          transition: 'all 0.15s ease',
          gap: '8px'
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', flex: 1 }}>
          {isMulti ? (
            Array.isArray(value) && value.length > 0 ? (
              value.map(opt => (
                <span 
                  key={opt.id} 
                  style={{
                    background: '#eff6ff',
                    color: '#1d4ed8',
                    border: '1px solid #bfdbfe',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                  }}
                >
                  <span>{opt.name || `Song #${opt.id}`}</span>
                  <span 
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(value.filter(item => String(item.id) !== String(opt.id)));
                    }}
                    style={{ 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      background: 'rgba(29, 78, 216, 0.12)',
                      color: '#1d4ed8',
                      fontSize: '11px',
                      fontWeight: '700'
                    }}
                  >
                    ✕
                  </span>
                </span>
              ))
            ) : (
              <span style={{ color: '#94a3b8', fontSize: '14px' }}>{placeholder}</span>
            )
          ) : (
            value ? (
              <span style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>{value.name}</span>
            ) : (
              <span style={{ color: '#94a3b8', fontSize: '14px' }}>{placeholder}</span>
            )
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isMulti && Array.isArray(value) && value.length > 0 && (
            <span 
              onClick={handleClearAll} 
              style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', cursor: 'pointer', paddingRight: '4px' }}
              title="Clear all"
            >
              Clear
            </span>
          )}
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="#64748b" 
            strokeWidth="2.5" 
            style={{ 
              flexShrink: 0,
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {/* Modern Floating Dropdown Menu */}
      {isOpen && (
        <div 
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 1000,
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            boxShadow: '0 12px 30px -4px rgba(15, 23, 42, 0.12), 0 4px 10px -2px rgba(15, 23, 42, 0.05)',
            overflow: 'hidden',
            animation: 'dropdownFadeIn 0.15s ease-out'
          }}
        >
          {/* Search Box Header */}
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input 
              type="text" 
              placeholder={`Search ${label.toLowerCase()}...`}
              value={search}
              onChange={handleSearchChange}
              style={{
                width: '100%',
                border: 'none',
                background: 'transparent',
                fontSize: '13px',
                color: '#0f172a',
                outline: 'none'
              }}
            />
          </div>

          {/* Selection counter header bar for multi-select */}
          {isMulti && Array.isArray(value) && value.length > 0 && (
            <div style={{ padding: '6px 14px', background: '#eff6ff', borderBottom: '1px solid #dbeafe', fontSize: '11px', fontWeight: '600', color: '#1d4ed8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{value.length} {value.length === 1 ? 'item' : 'items'} selected</span>
              <span onClick={handleClearAll} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Clear selection</span>
            </div>
          )}

          {/* Options List */}
          <div 
            onScroll={handleScroll}
            style={{ maxHeight: '220px', overflowY: 'auto', padding: '4px 0' }}
          >
            {options.length > 0 ? (
              options.map(opt => {
                const selected = isSelected(opt.id);
                return (
                  <div
                    key={opt.id}
                    onClick={() => handleSelectOption(opt)}
                    style={{
                      padding: '9px 16px',
                      fontSize: '13px',
                      fontWeight: selected ? '600' : '400',
                      color: selected ? '#2563eb' : '#0f172a',
                      background: selected ? '#eff6ff' : 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justify: 'space-between',
                      transition: 'background-color 0.12s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!selected) e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      if (!selected) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {isMulti && (
                        <div 
                          style={{ 
                            width: '18px', 
                            height: '18px', 
                            borderRadius: '4px', 
                            border: selected ? 'none' : '1px solid #cbd5e1',
                            backgroundColor: selected ? '#3b82f6' : '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justify: 'center',
                            color: '#ffffff',
                            transition: 'all 0.12s ease'
                          }}
                        >
                          {selected && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3.5">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      )}
                      <span>{opt.name}</span>
                    </div>

                    {!isMulti && selected && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                );
              })
            ) : (
              <div style={{ padding: '16px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                {isLoading ? 'Loading options...' : 'No options found'}
              </div>
            )}
            {isLoading && options.length > 0 && (
              <div style={{ padding: '8px', fontSize: '12px', color: '#64748b', textAlign: 'center', background: '#f8fafc' }}>
                Loading more...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AlbumForm({ existingAlbum, onSave, onCancel }) {
  const [name, setName] = useState('');
  const [releaseYear, setReleaseYear] = useState('');
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const parseLabelObj = (obj) => {
      if (!obj) return null;
      const id = obj.recordLabelId || obj.record_label_id || obj.label_id;
      const labelName = obj.recordLabelName || obj.labelName || obj.label;
      if (id || labelName) {
        return {
          id: id || null,
          name: labelName || 'Record Label'
        };
      }
      if (obj.id && obj.name && !obj.rawName && !obj.songs && !obj.songCount) {
        return { id: obj.id, name: obj.name };
      }
      return null;
    };

    const parseSongObj = (s) => {
      if (!s) return null;
      if (typeof s === 'object') {
        const id = s.id || s.song_id || s.songId;
        const name = s.name || s.display_name || s.title || s.rawName || (id ? `Song #${id}` : '');
        return id ? { id, name: name || `Song #${id}` } : null;
      }
      return { id: s, name: `Song #${s}` };
    };

    if (existingAlbum) {
      setName(existingAlbum.name || existingAlbum.title || existingAlbum.rawName || '');
      setReleaseYear(existingAlbum.release_year || existingAlbum.releaseYear || '');
      const img = existingAlbum.imageUrl || existingAlbum.image_url;
      if (img) {
        setUploadedLogo({
          name: 'Existing Image',
          size: 0,
          previewUrl: img
        });
      }
      
      const initialLabel = parseLabelObj(existingAlbum);
      if (initialLabel) setSelectedLabel(initialLabel);

      if (Array.isArray(existingAlbum.songs)) {
        const parsedSongs = existingAlbum.songs.map(parseSongObj).filter(Boolean);
        setSelectedSongs(parsedSongs);
      }

      // Always fetch full details and attached songs from backend when editing an album by ID
      if (existingAlbum.id) {
        const fetchDetails = async () => {
          try {
            const res = await apiClient(GET_ALBUM_DETAILS_URL(existingAlbum.id));
            if (res && isMounted) {
              if (res.name) setName(res.name);
              if (res.release_year || res.releaseYear) setReleaseYear(res.release_year || res.releaseYear);
              
              const fullLabel = parseLabelObj(res) || initialLabel;
              if (fullLabel) setSelectedLabel(fullLabel);

              let songsArr = res.songs;
              if (!Array.isArray(songsArr) || songsArr.length === 0) {
                // Fallback fetch from GET_ALBUM_SONGS_URL
                const songsRes = await apiClient(`${GET_ALBUM_SONGS_URL(existingAlbum.id)}?limit=1000`);
                if (songsRes && Array.isArray(songsRes.songs)) {
                  songsArr = songsRes.songs;
                }
              }

              if (Array.isArray(songsArr) && songsArr.length > 0 && isMounted) {
                const fullSongs = songsArr.map(parseSongObj).filter(Boolean);
                setSelectedSongs(fullSongs);
              }
            }
          } catch (err) {
            console.error('Failed to fetch album details in edit mode:', err);
          }
        };
        fetchDetails();
      }
    }

    return () => {
      isMounted = false;
    };
  }, [existingAlbum]);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const validateFile = (file) => {
    if (!file) return false;
    if (file.size > 5 * 1024 * 1024) {
      toast.warning(t('albums.validationLogoRange') || 'File size must be smaller than 5MB.');
      return false;
    }
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.pdf'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      toast.warning(t('albums.validationLogoType') || 'File must be PNG, JPG, JPEG or PDF.');
      return false;
    }
    return true;
  };

  const processFile = (file) => {
    const isPDF = file.name.toLowerCase().endsWith('.pdf');
    if (isPDF) {
      setUploadedLogo({
        file,
        name: file.name,
        size: file.size,
        previewUrl: null,
        isPDF: true
      });
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedLogo({
          file,
          name: file.name,
          size: file.size,
          previewUrl: event.target.result,
          isPDF: false
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        processFile(file);
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        processFile(file);
      }
    }
  };

  const handleRemoveFile = () => {
    setUploadedLogo(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.warning('Please enter album name.');
      return;
    }

    if (!selectedLabel) {
      toast.warning('Please select a record label.');
      return;
    }

    const payload = {
      name: name.trim(),
      release_year: releaseYear ? releaseYear.trim() : null,
      image_url: uploadedLogo ? uploadedLogo.previewUrl : null,
      record_label_id: selectedLabel.id,
      song_ids: selectedSongs.map(s => s.id)
    };

    onSave(payload);
  };

  return (
    <div className="recode-label-form-wrapper" style={{ padding: '24px', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
      {/* Breadcrumbs Nav */}
      <div className="breadcrumb-nav" style={{ marginBottom: '16px', display: 'flex', gap: '8px', fontSize: '14px' }}>
        <span className="breadcrumb-link" onClick={onCancel} style={{ cursor: 'pointer', color: '#64748b' }}>
          Albums
        </span>
        <span className="breadcrumb-separator" style={{ color: '#cbd5e1' }}>&gt;</span>
        <span className="breadcrumb-current" style={{ fontWeight: '600', color: '#0f172a' }}>
          {existingAlbum ? 'Edit Album' : 'Add New Album'}
        </span>
      </div>

      {/* Header Row */}
      <div className="add-document-header-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div className="header-icon-box" style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="12" cy="12" r="4" />
          </svg>
        </div>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
            {existingAlbum ? 'Edit Album' : 'Add New Album'}
          </h1>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            Enter the album details and associate songs.
          </p>
        </div>
      </div>

      {/* Form Body */}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Album Name */}
        <div className="form-field-group">
          <label className="form-field-label" style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
            Album Name <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            className="form-text-input"
            placeholder="Enter album name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
          />
        </div>

        {/* Release Year */}
        <div className="form-field-group">
          <label className="form-field-label" style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
            Release Year
          </label>
          <input
            type="text"
            className="form-text-input"
            placeholder="e.g. 2024"
            value={releaseYear}
            onChange={(e) => setReleaseYear(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
            maxLength="4"
            style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
          />
        </div>

        {/* Record Label Dropdown (Single Select, Lazy Loaded on Click) */}
        <LazyDropdown 
          label="Record Label"
          placeholder="Select Record Label"
          fetchUrl={GET_RECORD_LABEL_DROPDOWN_URL}
          isMulti={false}
          value={selectedLabel}
          onChange={(val) => setSelectedLabel(val)}
        />

        {/* Songs Dropdown (Multi Select, Lazy Loaded on Click) */}
        <LazyDropdown 
          label="Songs"
          placeholder="Select Songs"
          fetchUrl={GET_SONG_DROPDOWN_URL}
          isMulti={true}
          value={selectedSongs}
          onChange={(val) => setSelectedSongs(val)}
        />

        {/* Cover Image Upload */}
        <div className="upload-container">
          <label className="form-field-label" style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' }}>
            Album Cover Image
          </label>

          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.pdf"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <div
            className={`upload-drop-zone ${isDragActive ? 'drag-active' : ''}`}
            onDragEnter={(e) => { e.preventDefault(); setIsDragActive(true); }}
            onDragOver={(e) => { e.preventDefault(); setIsDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragActive(false); }}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
            style={{
              border: '2px dashed #cbd5e1',
              borderRadius: '12px',
              padding: '24px',
              textAlign: 'center',
              cursor: 'pointer',
              background: isDragActive ? '#f0f9ff' : '#f8fafc'
            }}
          >
            <div className="upload-content-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" style={{ marginBottom: '8px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <p style={{ margin: 0, fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                Drag & drop your cover image here or <span style={{ color: '#3b82f6', fontWeight: '600' }}>Choose File</span>
              </p>
              <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>
                Supported formats: PNG, JPG, JPEG, PDF (Max 5MB)
              </span>
            </div>
          </div>

          {/* Uploaded File Preview */}
          {uploadedLogo && (
            <div className="uploaded-file-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', marginTop: '12px', background: '#ffffff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {uploadedLogo.previewUrl && !uploadedLogo.isPDF ? (
                  <div style={{ width: '40px', height: '40px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
                    <img src={uploadedLogo.previewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                )}
                <div>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', display: 'block' }}>{uploadedLogo.name}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '18px' }}
              >
                ×
              </button>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '12px' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#0f172a', fontWeight: '600', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#ffffff', fontWeight: '600', cursor: 'pointer' }}
          >
            {existingAlbum ? 'Update Album' : 'Save Album'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AlbumForm;
